import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    BaselineType,
    IBaselineBurstResponse,
    ISpectrumChartResponse,
    IUnitResponse,
} from '@kcftech/prometheus-client';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { BurstDataType } from 'pages/workbench/types';
import { AxisChartSeriesData, Point } from 'shared/chart/core/types';
import { queryClient } from 'utils/query';

const queryKey = 'time-domain-burst-data';

export function invalidateTimeDomainData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

type UseTimeDomainDataDataOptions = {
    baseline: IBaselineBurstResponse | undefined;
    dataType: BurstDataType;

    // When using data platform data, waveform and units are loaded in TrendTab
    useDataPlatformData?: boolean;
    waveform?: DataPlatformApi.WaveformResponse;
    units?: IUnitResponse[];
};

export type SeriesData = Pick<
    AxisChartSeriesData,
    'dataPoints' | 'seriesMaxY' | 'seriesMinY' | 'seriesUnit' | 'seriesId'
>;

export type TimeDomainData = Readonly<{
    timeData: SeriesData;
    baselineSeriesData: SeriesData | undefined;
    isVibrationType: boolean;
}>;

function mapBurstDataToPoints(data: number[], rate: number): Point[] {
    return data.map((amplitude, index) => ({
        y: amplitude,
        x: (index * 1000) / rate,
    }));
}

type MapSeriesInfo = Readonly<{
    data: number[];
    rate: number;
    units: string | undefined;
    seriesId: string;
}>;

function mapSeries({ data, rate, units, seriesId }: MapSeriesInfo): SeriesData {
    const dataPoints = mapBurstDataToPoints(data, rate);

    return {
        seriesId,
        seriesUnit: units,
        seriesMinY: Math.min(...data),
        seriesMaxY: Math.max(...data),
        dataPoints,
    };
}

function mapTimeData(
    data: ISpectrumChartResponse,
    dataType: BurstDataType,
    baseline: IBaselineBurstResponse | undefined,
    indicatorId: string,
): TimeDomainData {
    const burstData = data.BurstData[0];
    const timeData = mapSeries({
        data: burstData.Data,
        rate: burstData.Rate,
        units: burstData.Units,
        seriesId: indicatorId,
    });

    const baselineData =
        dataType === 'acceleration'
            ? baseline?.BaselineBurstData.AccelTime
            : baseline?.BaselineBurstData.VelocityTime;

    // Ranged baselines should not appear on the time waveform plot because they do
    // not provide helpful information to the user. This could change in the future,
    // but for now we disable it.
    const baselineSeriesData =
        baseline && baseline.BaselineType === BaselineType.Single && baselineData
            ? mapSeries({
                  data: baselineData.Data,
                  rate: baselineData.Rate,
                  units: baselineData.Units,
                  seriesId: `${indicatorId}-baseline`,
              })
            : undefined;

    const isVibrationType = data.IsVibrationTypeIndicator;

    return { timeData, baselineSeriesData, isVibrationType };
}

function mapDataPlatformData(
    waveform: DataPlatformApi.WaveformResponse | undefined,
    baseline: IBaselineBurstResponse | undefined,
    dataType: BurstDataType,
    units: IUnitResponse[] | undefined,
    indicatorId: string,
): TimeDomainData | undefined {
    if (!waveform) {
        return undefined;
    }

    // If there's only one data series, use it. Otherwise, find the one that matches the requested data type.
    // This handles the case where a burst may not be in acceleration or velocity (e.g. pressure bursts).
    const data =
        waveform.data.length === 1
            ? waveform.data[0]
            : waveform.data.find((series) => series.dataType === dataType);

    if (!data) {
        throw new Error(`Failed to find waveform data of type ${dataType}`);
    }

    const mappedUnits =
        units?.find((unit) => unit.Code === data.units)?.Abbreviation ?? data.units;

    const timeData = mapSeries({
        data: data.waveform,
        rate: waveform.sampleRate,
        units: mappedUnits,
        seriesId: indicatorId,
    });
    const baselineData =
        dataType === 'acceleration'
            ? baseline?.BaselineBurstData.AccelTime
            : baseline?.BaselineBurstData.VelocityTime;

    // Ranged baselines should not appear on the time waveform plot because they do
    // not provide helpful information to the user. This could change in the future,
    // but for now we disable it.
    const baselineSeriesData =
        baseline && baseline.BaselineType === BaselineType.Single && baselineData
            ? mapSeries({
                  data: baselineData.Data,
                  rate: baselineData.Rate,
                  units: baselineData.Units,
                  seriesId: `${indicatorId}-baseline`,
              })
            : undefined;

    const isVibrationType =
        data.dataType === 'acceleration' || data.dataType === 'velocity';

    return {
        timeData,
        baselineSeriesData,
        isVibrationType,
    };
}

export function useTimeDomainData(
    indicatorId: string,
    time: Date,
    options: UseTimeDomainDataDataOptions,
    overrides?: Omit<UseQueryOptions<TimeDomainData | undefined>, 'queryKey' | 'queryFn'>,
) {
    return useQuery({
        queryKey: [queryKey, indicatorId, time, options],

        queryFn: async () => {
            if (options.useDataPlatformData) {
                return mapDataPlatformData(
                    options.waveform,
                    options.baseline,
                    options.dataType,
                    options.units,
                    indicatorId,
                );
            }

            const burstData = await sdClient.getBurstData({
                indicatorId,
                includeBaseline: false,
                returnTimeDomain: true,
                returnVelocity: options.dataType === 'velocity',
                dateTimestamp: time,
            });

            return mapTimeData(
                burstData,
                options.dataType,
                options.baseline,
                indicatorId,
            );
        },

        ...overrides,
        enabled:
            (overrides?.enabled ?? true) &&
            (!options.useDataPlatformData || options.waveform !== undefined),
    });
}
